// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Tracks, per round, whether the user has already handled (saved or skipped) the optional Mystery
// prize setup. The one-time redirect guards in the hole/round-start screens use this so they don't
// keep bouncing the user back to the mystery-prize screen once they've chosen to proceed — mystery
// setup is optional and may be left empty.

type AsyncStorageLike = {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
};

let asyncStorage: AsyncStorageLike | null = null;

try {
  asyncStorage = require('@react-native-async-storage/async-storage').default as AsyncStorageLike;
} catch {
  asyncStorage = null;
}

const getLocalStorage = () => (typeof window !== 'undefined' ? window.localStorage : null);

const keyFor = (roundId: string) => `goffy:mystery-ack:${roundId}`;

export const markMysteryAcknowledged = async (roundId: string): Promise<void> => {
  const normalized = roundId?.trim();
  if (!normalized) return;
  const key = keyFor(normalized);
  if (asyncStorage) {
    try {
      await asyncStorage.setItem(key, '1');
      return;
    } catch {
      // Fall back to localStorage below.
    }
  }
  try {
    getLocalStorage()?.setItem(key, '1');
  } catch {
    // Best effort: if persistence fails the guard may re-prompt once, which is acceptable.
  }
};

export const isMysteryAcknowledged = async (roundId: string): Promise<boolean> => {
  const normalized = roundId?.trim();
  if (!normalized) return false;
  const key = keyFor(normalized);
  if (asyncStorage) {
    try {
      return (await asyncStorage.getItem(key)) === '1';
    } catch {
      // Fall back to localStorage below.
    }
  }
  try {
    return getLocalStorage()?.getItem(key) === '1';
  } catch {
    return false;
  }
};
